import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuthModal } from '../components/AuthModal';
import { useAuth } from '../contexts/AuthContext';

export function GuessChallenge() {
  const { id } = useParams();
  const { isAuthenticated, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [guessed, setGuessed] = useState(false);
  const [selectedSide, setSelectedSide] = useState<'left' | 'right' | null>(null);

  // Decode the comparison data from the ID
  // Format: {leftScore}-{rightScore}-{context}
  const parts = (id || '').split('-');
  const leftScore = parseInt(parts[0]) || 70;
  const rightScore = parseInt(parts[1]) || 85;
  const context = parts[2] || 'dating';
  const winner = leftScore > rightScore ? 'left' : 'right';

  return (
    <div className="min-h-screen bg-cream relative overflow-hidden">
      <div className="page-orb left-[-5rem] top-24 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-4rem] top-28 h-72 w-72 bg-info/10" />
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        onEmailSignIn={signInWithEmail}
        onEmailSignUp={signUpWithEmail}
      />

      <div className="pt-20 pb-16 px-5 max-w-2xl mx-auto text-center">
        <div className="page-hero-panel mb-8">
          <div className="premium-kicker mb-3">Guess challenge</div>
          <h1 className="font-serif text-4xl text-ink mb-2" style={{ fontWeight: 600 }}>
            Which photo scored higher?
          </h1>
          <p className="hero-copy max-w-xl mx-auto">
            Tap the photo you think the AI rated higher, then use the result to pull friends into the same premium photo audit flow.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-8">
          {/* Left photo placeholder */}
          <button
            onClick={() => { setSelectedSide('left'); setGuessed(true); }}
            disabled={guessed}
            className={`aspect-square bg-cream-warm rounded-[28px] flex items-center justify-center text-4xl transition-all shadow-card ${
              guessed && selectedSide === 'left' ? (winner === 'left' ? 'ring-4 ring-success' : 'ring-4 ring-rose') : ''
            } ${guessed && selectedSide !== 'left' ? 'opacity-60' : ''}`}
          >
            {guessed ? (
              <span className={`text-2xl font-sans ${winner === 'left' ? 'text-success' : 'text-rose'}`} style={{ fontWeight: 600 }}>
                {leftScore}
              </span>
            ) : (
              <span className="text-ink-faint font-sans" style={{ fontWeight: 500 }}>A</span>
            )}
          </button>

          {/* Right photo placeholder */}
          <button
            onClick={() => { setSelectedSide('right'); setGuessed(true); }}
            disabled={guessed}
            className={`aspect-square bg-cream-warm rounded-[28px] flex items-center justify-center text-4xl transition-all shadow-card ${
              guessed && selectedSide === 'right' ? (winner === 'right' ? 'ring-4 ring-success' : 'ring-4 ring-rose') : ''
            } ${guessed && selectedSide !== 'right' ? 'opacity-60' : ''}`}
          >
            {guessed ? (
              <span className={`text-2xl font-sans ${winner === 'right' ? 'text-success' : 'text-rose'}`} style={{ fontWeight: 600 }}>
                {rightScore}
              </span>
            ) : (
              <span className="text-ink-faint font-sans" style={{ fontWeight: 500 }}>B</span>
            )}
          </button>
        </div>

        {guessed && (
          <div className="animate-fade-in">
            <p className="text-sm text-ink-muted mb-2">
              {selectedSide === winner ? 'You got it right!' : 'Not quite! The other photo scored higher.'}
            </p>
            <p className="text-xs text-ink-faint mb-8">
              AI ratings are based on {context === 'dating' ? 'attractiveness, trustworthiness, and likability' : context === 'business' ? 'competence, influence, and approachability' : 'fun, confidence, and authenticity'}.
            </p>

            <div className="card p-6 mb-6 lift-hover">
              <h3 className="font-sans text-lg text-ink mb-2" style={{ fontWeight: 600 }}>
                Want to know YOUR photo score?
              </h3>
              <p className="text-sm text-ink-muted mb-4">
                3 AI models rate your photo in 15 seconds. Free to try.
              </p>
              <button
                onClick={() => isAuthenticated ? (window.location.href = '/app') : setAuthOpen(true)}
                className="btn-rose text-sm w-full"
              >
                Rate my photo free
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
